import { useState } from 'react';
import { Button } from '@leadsnavi/ui-kit';
import validator from 'validator';
import cn from 'classnames';
import type { SignUpStepProps } from '../types';
import { GoogleAuthButton, OrDivider, FormField, PasswordInput, PasswordRules, PrivacyCheckbox } from '../shared';
import { usePasswordValidation, isPasswordValid } from '../hooks/usePasswordValidation';

/**
 * 注册步骤页面
 */
export function SignUpStep({
  errors: externalErrors,
  loading = false,
  googleAuthUrl,
  onStepChange,
  onPrivacyPolicyClick,
  onSubmit,
}: SignUpStepProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [agreedToPolicy, setAgreedToPolicy] = useState(false);
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});

  const errors = { ...localErrors, ...externalErrors };
  const passwordValidation = usePasswordValidation(password);

  /**
   * 验证表单
   */
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!validator.isEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (!isPasswordValid(passwordValidation)) {
      newErrors.password = 'Password does not meet requirements';
    }

    setLocalErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * 提交表单
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit?.(email, password, agreedToPolicy);
    }
  };

  const canSubmit = agreedToPolicy && isPasswordValid(passwordValidation) && email;

  return (
    <form onSubmit={handleSubmit} className="flex w-full flex-col gap-[32px]">
      {/* 标题 */}
      <div className="flex flex-col items-start">
        <h2 className="text-[30px] font-semibold leading-none tracking-[-0.3px] text-[#09090b]">Sign up</h2>
      </div>

      {/* 表单内容 */}
      <div className="flex flex-col gap-[24px]">
        {/* Google 登录 + OR 分隔线 */}
        <div className="flex flex-col gap-[24px]">
          <GoogleAuthButton text="Sign up with Google" href={googleAuthUrl} />
          <OrDivider />
        </div>

        {/* 邮箱 + 密码输入 */}
        <div className="flex flex-col gap-[20px]">
          <FormField
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            error={errors.email}
            disabled={loading}
          />

          {/* 密码 */}
          <div className="flex flex-col gap-[12px]">
            <PasswordInput
              label="Password"
              value={password}
              onChange={setPassword}
              error={errors.password}
              disabled={loading}
            />
            <PasswordRules validation={passwordValidation} />
          </div>
        </div>

        {/* Create account 按钮 + 隐私政策 */}
        <div className="flex flex-col items-center gap-[20px]">
          <Button
            type="submit"
            disabled={loading || !canSubmit}
            loading={loading}
            className={cn(
              'h-[40px] w-full rounded-[8px] bg-[#171717] px-[24px] py-[9.5px]',
              'text-[14px] font-medium leading-[1.5] tracking-[0.07px] text-white',
              'hover:bg-[#262626]',
              'disabled:cursor-not-allowed disabled:opacity-50'
            )}
          >
            Create account
          </Button>

          <PrivacyCheckbox
            checked={agreedToPolicy}
            onChange={setAgreedToPolicy}
            onPrivacyPolicyClick={onPrivacyPolicyClick}
          />
        </div>
      </div>

      {/* 切换到登录 */}
      <div className="flex items-center justify-center gap-[8px] text-[14px] leading-[1.5] tracking-[0.07px]">
        <span className="text-[#525252]">Have an account already?</span>
        <button
          type="button"
          onClick={() => onStepChange?.('login')}
          className="text-[#10b981] underline hover:text-[#059669]"
        >
          Log in
        </button>
      </div>
    </form>
  );
}

export default SignUpStep;
